import { useCallback, useEffect, useMemo, useState } from "react";
import { BezierProps, InternalBezier } from "../types/bezier-types";
import { PresetObj, ToolApplyRequest } from "../types/general-types";
import { PresetItem } from "./PresetItem";
import HorizontalScroll from "./HorizontalScroll";
import { StyledScrollbarVertical } from "../styles/StyledScrollbarVertical";
import IconNavArrowDown from "./icons/IconNavArrowDown";
import {
	applyTool,
	loadFavoritePresets,
	saveFavoritePresets,
} from "../utilities/layers";
import {
	savePresetOrdering,
	loadCustomPresets,
	saveCustomPresets,
} from "../utilities/layers";
import { buildToolPresets } from "../utilities/build-presets";
import { UITools } from "../types/context-types";
import { useInputContext } from "leva/plugin";
import { useGlobalState } from "../hooks/useGlobalState";
import { useNotifications } from "../context/NotificationProvider";

interface PresetsBezierBarProps {
	apply: (values: InternalBezier) => void;
	presetBezierBarOpen: boolean;
	presetBezierBarFull: boolean;
	presetRows: PresetObj[][];
	setPresetRows: React.Dispatch<React.SetStateAction<PresetObj[][]>>;
	setBezierFavorites: React.Dispatch<React.SetStateAction<PresetObj[][]>>;
	favorites: PresetObj[];
}

export function PresetsBezierBar({
	presetRows,
	setPresetRows,
	favorites,
	setBezierFavorites,
	presetBezierBarOpen,
	presetBezierBarFull,
	apply,
}: PresetsBezierBarProps) {
	const [presetSearchInput, setPresetSearchInput] = useState<string>("");
	const [presetCategory, setPresetCategory] = useState<string>("Favorites");
	const { label, value, displayValue, settings, onUpdate, setSettings } =
		useInputContext<BezierProps>();
	const { configSettings, UIStatus, synergy, keysHeld } = useGlobalState();
	const { addNotification } = useNotifications();

	const handleSVGClick = (value: number[]) => {
		// console.log(value);
		//@ts-ignore
		if (keysHeld.shift || presetBezierBarFull) return apply(value);
		//@ts-ignore
		onUpdate(value);
	};

	function handlePresetSectionMove(index: number, moveUp: boolean): void {
		// No move is needed if attempting to move up when already at the top,
		// or attempting to move down when already at the bottom.
		if ((moveUp && index === 0) || (!moveUp && index === presetRows.length - 1))
			return;

		let newSectionOrder: PresetObj[][] = [];
		setPresetRows((prevSectionOrder) => {
			newSectionOrder = [...prevSectionOrder]; // Create a new copy of the section order array.
			const swapIndex = moveUp ? index - 1 : index + 1;
			[newSectionOrder[swapIndex], newSectionOrder[index]] = [
				newSectionOrder[index],
				newSectionOrder[swapIndex],
			];
			return newSectionOrder;
		});
		let presetSectionOrder: string[] = [];
		newSectionOrder.forEach((section) => {
			presetSectionOrder.push(section[0].section);
		});
		savePresetOrdering(presetSectionOrder, "bezier");
	}

	function deleteCustomPreset(presetName: string): void {
		let res: JSX.Element[] = [];
		loadCustomPresets().then((customPresets) => {
			customPresets = JSON.parse(customPresets);
			//@ts-ignore
			res = customPresets.filter(
				(preset: PresetObj) => preset.name !== presetName
			);
			//@ts-ignore
			saveCustomPresets(res).then(() => {
				deleteFavoritePreset(presetName);
			});
		});
	}

	function deleteFavoritePreset(presetName: string): void {
		loadFavoritePresets().then((favorites) => {
			let favoritesObj = JSON.parse(favorites);
			delete favoritesObj[presetName];
			saveFavoritePresets(favoritesObj).then(() => {
				buildPresets();
			});
		});
	}

	const handleFavoriteClick = (name: string): void => {
		loadFavoritePresets().then((favorites) => {
			let favoritesObj = JSON.parse(favorites);
			if (favoritesObj[name]) {
				delete favoritesObj[name];
			} else {
				favoritesObj[name] = true;
			}
			saveFavoritePresets(favoritesObj).then(() => {
				buildPresets();
			});
		});
	};

	function buildPresets() {
		buildToolPresets(UITools.bezier, setPresetRows, setBezierFavorites);
	}

	return (
		<div
			className={`flex w-full items-start transition-all ease-out ${
				presetBezierBarOpen
					? presetBezierBarFull
						? "h-[calc(100vh-80px)] duration-150"
						: "h-20 duration-150"
					: "h-0 duration-150"
			}`}
		>
			{/* Category select section converted to Tailwind CSS */}
			<div
				className={`flex flex-col pl-[2px] pr-1 h-full justify-center bg-elevation2 transition-all duration-200 ${
					presetBezierBarOpen ? "opacity-1" : "opacity-0"
				}`}
			>
				<StyledScrollbarVertical>
					<MenuListItem
						onClick={() => setPresetCategory("Favorites")}
						label={"Favorites"}
						presetCategory={presetCategory}
						presetBezierBarFull={presetBezierBarFull}
						showMoveArrows={false}
						index={0}
						handlePresetSectionMove={handlePresetSectionMove}
					></MenuListItem>
					{presetRows.map((presetArr: PresetObj[], index) => (
						<MenuListItem
							onClick={() => setPresetCategory(presetArr[0].section)}
							label={presetArr[0].section}
							presetCategory={presetCategory}
							presetBezierBarFull={presetBezierBarFull}
							showMoveArrows={presetBezierBarFull}
							index={index}
							handlePresetSectionMove={handlePresetSectionMove}
						></MenuListItem>
					))}
				</StyledScrollbarVertical>
			</div>
			<div className="bg-elevation3 flex-1 h-full w-[calc(100vw-72px)] rounded-tl-md">
				{!presetBezierBarFull ? (
					<HorizontalScroll>
						<div className="flex flex-row items-start h-full gap-2 pt-2.5 px-1">
							{presetCategory === "Favorites"
								? favorites
										.filter((preset) =>
											preset.name
												.toLowerCase()
												.includes(presetSearchInput.toLowerCase())
										)
										.map((preset) => (
											<PresetItem
												key={preset.index}
												index={preset.index}
												value={preset.value}
												name={preset.name}
												handleSVGClick={handleSVGClick}
												handleFavoriteClick={handleFavoriteClick}
												disallowFavorite={true}
												isFavorite={true}
											/>
										))
								: presetRows
										.filter(
											(presetArr) => presetArr[0].section === presetCategory
										)
										.map((presetArr) =>
											presetArr
												.filter((preset) =>
													preset.name
														.toLowerCase()
														.includes(presetSearchInput.toLowerCase())
												)
												.map((preset) => (
													<PresetItem
														key={preset.index}
														index={preset.index}
														value={preset.value}
														name={preset.name}
														handleSVGClick={handleSVGClick}
														handleFavoriteClick={handleFavoriteClick}
														disallowFavorite={true}
														isStandard={preset.isStandard}
														isFavorite={preset.isFavorite}
														deleteCustomPreset={deleteCustomPreset}
													/>
												))
										)}
						</div>
					</HorizontalScroll>
				) : (
					<StyledScrollbarVertical>
						<div className="flex flex-wrap gap-1.5 justify-start px-2 py-2 w-full h-fit">
							{presetCategory === "Favorites"
								? favorites
										.filter((preset) =>
											preset.name
												.toLowerCase()
												.includes(presetSearchInput.toLowerCase())
										)
										.map((preset) => (
											<PresetItem
												key={preset.index}
												index={preset.index}
												value={preset.value}
												name={preset.name}
												handleSVGClick={handleSVGClick}
												handleFavoriteClick={handleFavoriteClick}
												disallowFavorite={true}
												isFavorite={preset.isFavorite}
											/>
										))
								: presetRows
										.filter(
											(presetArr) => presetArr[0].section === presetCategory
										)
										.map((presetArr) =>
											presetArr
												.filter((preset) =>
													preset.name
														.toLowerCase()
														.includes(presetSearchInput.toLowerCase())
												)
												.map((preset) => (
													<PresetItem
														key={preset.index}
														index={preset.index}
														value={preset.value}
														name={preset.name}
														handleSVGClick={handleSVGClick}
														handleFavoriteClick={handleFavoriteClick}
														disallowFavorite={false}
														isStandard={preset.isStandard}
														isFavorite={preset.isFavorite}
														deleteCustomPreset={deleteCustomPreset}
													/>
												))
										)}
						</div>
					</StyledScrollbarVertical>
				)}
			</div>
		</div>
	);
}

const MenuListItem = ({
	onClick,
	label,
	presetCategory,
	showMoveArrows,
	presetBezierBarFull,
	index,
	handlePresetSectionMove,
}: {
	onClick: () => void;
	label: string;
	presetCategory: string;
	showMoveArrows?: boolean;
	presetBezierBarFull: boolean;
	index: number;
	handlePresetSectionMove: (index: number, moveUp: boolean) => void;
}) => {
	return (
		<div
			className={`flex w-full pl-2 pr-1 items-center gap-1 text-highlight1 cursor-pointer rounded hover:text-highlight3
			${presetCategory === label ? "bg-elevation3 text-highlight2" : ""} 
			${presetBezierBarFull ? "h-[28px]" : "h-fit"}
			`}
			onClick={onClick}
		>
			{/* {label} */}
			<span className="max-w-[54px] text-xs truncate">{label}</span>
			{showMoveArrows && (
				<div className="ml-auto flex flex-col items-center">
					<div
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							handlePresetSectionMove(index, true);
						}}
						className={`w-4 h-4 -mb-[2px] text-wht-400 hover:text-wht-100 cursor-pointer -scale-100 ${
							index === 0 ? "opacity-50 pointer-events-none" : ""
						}`}
					>
						<IconNavArrowDown />
					</div>
					<div
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							handlePresetSectionMove(index, false);
						}}
						className="w-4 h-4 -mt-[2px] text-wht-400 hover:text-wht-100 cursor-pointer"
					>
						<IconNavArrowDown />
					</div>
				</div>
			)}
		</div>
	);
};
